// src/App.jsx
import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation, useParams, Link } from 'react-router-dom';
import { siteConfig } from './data/personalInfo';
import Header from './components/layout/Header';
import Footer from './components/layout/Footer';
import HomePage from './components/sections/HomePage';
import WorkExperience from './components/sections/WorkExperience';
import CoursesSection from './components/sections/CoursesSection';
import ProjectsSection from './components/sections/ProjectsSection';
import GallerySection from './components/sections/GallerySection';
import SoleMateShowcase from './components/pages/SoleMateShowcase';
import XRThesisShowcase from './components/pages/XRThesisShowcase';
import Connect2Showcase from './components/pages/Connect2Showcase';
import EverythingScienceShowcase from './components/pages/EverythingScienceShowcase';
import DungeonShowcase from './components/pages/DungeonShowcase';
import KesslerShowcase from './components/pages/KesslerShowcase';
import SecretLinks from './components/pages/SecretLinks';
import ResumeViewer from './components/pages/ResumeViewer';
import ThesisViewer from './components/pages/ThesisViewer';

// Portfolio component for the main site
const Portfolio = () => {
  const [activeSection, setActiveSection] = useState('home');
  const [theme, setTheme] = useState(siteConfig.defaultTheme);
  
  // Initialize theme from localStorage or system preference
  useEffect(() => {
    const savedTheme = localStorage.getItem('theme') || 
      (window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light');
    
    setTheme(savedTheme);
    applyTheme(savedTheme);
  }, []);
  
  // Apply theme to document
  const applyTheme = (newTheme) => {
    document.documentElement.classList.toggle('dark', newTheme === 'dark');
    localStorage.setItem('theme', newTheme);
  };
  
  // Toggle theme function
  const toggleTheme = () => {
    const newTheme = theme === 'dark' ? 'light' : 'dark';
    setTheme(newTheme);
    applyTheme(newTheme);
  };
  
  // Render the appropriate section based on activeSection state
  const renderSection = () => {
    switch(activeSection) {
      case 'home':
        return <HomePage setActiveSection={setActiveSection} />;
      case 'work':
        return <WorkExperience />;
      case 'courses':
        return <CoursesSection />;
      case 'projects':
        return <ProjectsSection />;
      case 'gallery':
        return <GallerySection />;
      default:
        return <HomePage setActiveSection={setActiveSection} />;
    }
  };
  
  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-gray-100 transition-colors duration-300">
      <Header 
        activeSection={activeSection} 
        setActiveSection={setActiveSection}
        theme={theme}
        toggleTheme={toggleTheme}
      />
      
      <main>
        {renderSection()}
      </main>
      
      <Footer setActiveSection={setActiveSection} />
    </div>
  );
};

// Generic project page wrapper with back button
const ProjectPageWrapper = ({ children }) => {
  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-gray-100 transition-colors duration-300">
      {/* Back to Portfolio button */}
      <div className="fixed top-4 left-4 z-50">
        <Link
          to="/"
          className="flex items-center space-x-2 bg-white/90 dark:bg-gray-800/90 backdrop-blur-sm text-gray-900 dark:text-white px-4 py-2 rounded-lg hover:bg-white dark:hover:bg-gray-800 transition-colors border border-gray-200 dark:border-gray-700 shadow-lg"
        >
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
          </svg>
          <span>Back to Portfolio</span>
        </Link>
      </div>
      
      {children}
    </div>
  );
};

// SoleMate page wrapper
const SoleMatePage = () => {
  return (
    <ProjectPageWrapper>
      <SoleMateShowcase />
    </ProjectPageWrapper>
  );
};

// XR Thesis page wrapper
const XRThesisPage = () => {
  return (
    <ProjectPageWrapper>
      <XRThesisShowcase />
    </ProjectPageWrapper>
  );
};

// Connect2 page wrapper
const Connect2Page = () => {
  return (
    <ProjectPageWrapper>
      <Connect2Showcase />
    </ProjectPageWrapper>
  );
};

// Everything Science page wrapper
const EverythingSciencePage = () => {
  return (
    <ProjectPageWrapper>
      <EverythingScienceShowcase />
    </ProjectPageWrapper>
  );
};

// Dungeon page wrapper
const DungeonPage = () => {
  return (
    <ProjectPageWrapper>
      <DungeonShowcase />
    </ProjectPageWrapper>
  );
};

// Kessler page wrapper
const KesslerPage = () => {
  return (
    <ProjectPageWrapper>
      <KesslerShowcase />
    </ProjectPageWrapper>
  );
};


function App() {
  return (
    <Router>
      <Routes>
        <Route path="/" element={<Portfolio />} />
        {/* Project showcase routes */}
        <Route path="/solemate" element={<SoleMatePage />} />
        <Route path="/xr-thesis" element={<XRThesisPage />} />
        <Route path="/connect2" element={<Connect2Page />} />
        <Route path="/everything-science" element={<EverythingSciencePage />} />
        <Route path="/dungeon" element={<DungeonPage />} />
        <Route path="/kessler" element={<KesslerPage />} />
        <Route path="/secret" element={<SecretLinks />} />
        <Route path="/resume" element={<ResumeViewer />} />
        <Route path="/thesis" element={<ThesisViewer />} />
      </Routes>
    </Router>
  );
}

export default App;